import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { IActiveFilter, FilterEnum, IVaultPasswordUrl, VaultPasswordExpireReason } from '@app/models';

class VaultPasswordsService {
    readonly apiBase: string = '/api/directory/Vaults';
    readonly migratorBase: string = '/api/directory/onpremmigrators';
    readonly pwsAgentBase: string = '/api/directory/PwSSyncAgentProfiles';

    getVaultPasswordsDTO(
        vaultId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        folderId: string,
        filters: IActiveFilter[]
    ): AxiosPromise {
        const newFilters: IActiveFilter[] = [...filters];

        newFilters.push({
            filterBy: 'FolderId',
            value: String(folderId),
            name: FilterEnum.equals,
            label: 'Folder'
        });

        return apiService
            .get(`${this.apiBase}(${vaultId})/GetVaultPasswordsForShortDisplayDTO`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    $filter: manageFilters(newFilters)
                }
            })
            .catch();
    }

    getProtectedPassword(vaultId: string, passwordId: string, passwordWanted: string): AxiosPromise {
        let jsonObject: any = {};
        jsonObject.PasswordId = passwordId;
        jsonObject.PasswordWanted = passwordWanted;

        return apiService.post(`${this.apiBase}(${vaultId})/GetProtectedPassword`, jsonObject).catch();
    }

    getExpiredPasswordRecord(vaultId: string, passwordId: string, onSucces: Function) {
        let jsonObject: any = {
            VaultPasswordId: passwordId
        };

        apiService.post(`${this.apiBase}(${vaultId})/GetExpiredPasswordRecord`, jsonObject, { onSuccess: onSucces });
    }

    getVaultPasswordByPasswordIdNoPwd(vaultId: string, vaultPasswordId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService
            .post(
                `${this.apiBase}(${vaultId})/GetVaultPasswordByPasswordIdNoPwd`,
                {
                    VaultPasswordId: vaultPasswordId
                },
                {
                    onError: handleError,
                    onSuccess: handleSuccess
                }
            )
            .catch();
    }

    getVaultPasswordByPasswordId(vaultId: string, vaultPasswordId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService
            .post(
                `${this.apiBase}(${vaultId})/GetVaultPasswordByPasswordId`,
                {
                    VaultPasswordId: vaultPasswordId
                },
                {
                    onError: handleError,
                    onSuccess: handleSuccess
                }
            )
            .catch();
    }

    deleteVaultPasswords(ids: string[], vaultId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .post(
                `${this.apiBase}(${vaultId})/DeleteVaultPassword`,
                {
                    VaultPasswords: ids
                },
                {
                    onSuccess: handleSuccess,
                    onError: handleError
                }
            )
            .catch();
    }

    orgCanMigrateAjax(handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService.get(`${this.migratorBase}/OrgCanMigrate`, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    getMigratorUrl(handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService.get(`${this.migratorBase}/GetMigratorUrl`, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    onPremMigration(fileData: object, secretKey: string, groupId: string, initialPolicyId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        let headersToSend: any = {};

        if (groupId !== '') {
            headersToSend = {
                'Content-Type': 'multipart/form-data',
                secretkey: secretKey,
                policyId: initialPolicyId,
                groupid: groupId
            };
        } else {
            headersToSend = {
                'Content-Type': 'multipart/form-data',
                secretkey: secretKey,
                policyId: initialPolicyId
            };
        }

        return apiService
            .post(this.migratorBase, fileData, {
                headers: headersToSend,
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getPasswordReminders(
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        organizationId: string,
        handleError: Function,
        handleSuccess: Function,
        isReset: boolean,
        filterText: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`${this.apiBase}/PasswordsExpiring`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    $filter: manageFilters(filters, organizationId),
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getGeneratedPassword(complexity: string, vaultId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .post(
                `${this.apiBase}(${vaultId})/GetGeneratedPassword`,
                {
                    Complexity: complexity
                },
                {
                    onSuccess: handleSuccess,
                    onError: handleError
                }
            )
            .catch();
    }

    updatePasswordOnly(jsonObject: any, vaultId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .post(`${this.apiBase}(${vaultId})/UpdatePasswordOnly`, jsonObject, {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    addVaultPassword(jsonObject: any, vaultId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .post(`${this.apiBase}(${vaultId})/AddPassword`, jsonObject, {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    updateVaultPassword(jsonObject: any, vaultId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .post(`${this.apiBase}(${vaultId})/UpdatePassword`, jsonObject, {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    removeFailedSyncFlag(jsonObject: any, vaultId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .post(`${this.apiBase}(${vaultId})/RemoveFailedSyncFlag`, jsonObject, {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    // AGENTS
    getAgents(
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        organizationId: string,
        handleError: Function,
        handleSuccess: Function,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(this.pwsAgentBase, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters, organizationId) } : { $filter: manageFilters([], organizationId) })
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getSyncAgentDownloadLocation() {
        return apiService.get('/api/pwssync').catch();
    }

    getNewSyncAgent(handleSuccess: Function) {
        return apiService
            .get(`${this.pwsAgentBase}/GetNewAgent`, {
                onSuccess: handleSuccess,
                // polling API, error is transient so we should still keep polling
                onError: handleSuccess
            })
            .catch();
    }

    putSyncAgent(syncAgentObject: any, agentId: string, handleSuccess: Function, handleError: Function) {
        return apiService
            .put(`${this.pwsAgentBase}(${agentId})`, syncAgentObject, {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    getSyncAgent(agentId: string, handleSuccess: Function, handleError: Function) {
        return apiService
            .get(`${this.pwsAgentBase}(${agentId})`, {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    patchSyncAgent(syncAgentObject: any, agentId: string, handleSuccess: Function, handleError: Function) {
        return apiService
            .patch(`${this.pwsAgentBase}(${agentId})`, syncAgentObject, {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    deleteAgent(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.pwsAgentBase}/DeleteProfiles`;

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    getPasswordRedirectUrl(pwdId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .get(`${this.apiBase}/getPasswordRedirectUrl`, {
                params: {
                    id: pwdId
                },
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    getVaultPasswordUrls(top: number, skip: number): AxiosPromise {
        return apiService.get(`${this.apiBase}/GetVaultPasswordUrls?$top=${top}&$skip=${skip}&$count=true&$orderBy=vaultid asc`);
    }

    getPasswordsInfo(top: number): Promise<IVaultPasswordUrl[]> {
        let values: IVaultPasswordUrl[] = [];
        let count = 0;

        const getVaultPasswordUrls = (): Promise<IVaultPasswordUrl[]> => {
            return this.getVaultPasswordUrls(top, count).then((resp: AxiosResponse) => {
                count += resp.data.value.length;
                values = [...values, ...resp.data.value];

                if (count < resp.data['@odata.count']) {
                    return getVaultPasswordUrls();
                } else {
                    return values;
                }
            });
        };

        return getVaultPasswordUrls();
    }

    expirePassword(vaultPasswordId: string, vaultId: string, reason: VaultPasswordExpireReason, handleSuccess: Function, handleError: Function) {
        return apiService
        .post(`${this.apiBase}(${vaultId})/ExpirePassword`, { VaultPasswordId: vaultPasswordId, Reason: reason }, {
            onSuccess: handleSuccess,
            onError: handleError
        })
        .catch();
    }
}

export const vaultPasswordsService = new VaultPasswordsService();
